import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import ImageUpload from '@/components/ImageUpload';
import BulkImport from '@/components/BulkImport';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emptyProduct = { name: '', description: '', category: 'gold', weight: 1, weight_unit: 'oz', premium_percentage: 5, image_url: '', status: 'active', featured: false, sort_order: 0, mint: '', purity: '', fixed_price_nzd: '', pricing_mode: 'quote' };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [newCat, setNewCat] = useState({ slug: '', label: '', sort_order: 10 });

  const fetchProducts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/products`);
      setProducts(data);
    } catch (err) {
      console.error('Failed to load products:', err);
      toast.error('Failed to load products');
    }
  }, []);
  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/categories`);
      setCategories(data);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, []);
  useEffect(() => { fetchProducts(); fetchCategories(); }, [fetchProducts, fetchCategories]);

  const openNew = () => { setEditing(null); setForm(emptyProduct); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      name: p.name, description: p.description, category: p.category,
      weight: p.weight, weight_unit: p.weight_unit, premium_percentage: p.premium_percentage,
      image_url: p.image_url || '', status: p.status, featured: p.featured || false,
      sort_order: p.sort_order || 0, mint: p.mint || '', purity: p.purity || '',
      fixed_price_nzd: p.fixed_price_nzd || '', pricing_mode: p.pricing_mode || 'quote',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const payload = { ...form, fixed_price_nzd: form.fixed_price_nzd ? parseFloat(form.fixed_price_nzd) : null };
    try {
      if (editing) {
        await axios.put(`${API}/admin/products/${editing}`, payload);
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/admin/products`, payload);
        toast.success('Product created');
      }
      setDialogOpen(false);
      fetchProducts();
    } catch (err) {
      console.error('Failed to save product:', err);
      toast.error('Failed to save product');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`);
      toast.success('Deleted');
      fetchProducts();
    } catch (err) {
      console.error('Failed to delete product:', err);
      toast.error('Failed to delete');
    }
  };

  const handleAddCategory = async () => {
    if (!newCat.slug || !newCat.label) { toast.error('Slug and label required'); return; }
    try {
      await axios.post(`${API}/admin/categories`, newCat);
      toast.success('Category added');
      setNewCat({ slug: '', label: '', sort_order: 10 });
      fetchCategories();
    } catch (err) {
      console.error('Failed to add category:', err);
      toast.error('Failed');
    }
  };

  const handleDeleteCategory = async (slug) => {
    if (!window.confirm(`Delete category "${slug}"?`)) return;
    try {
      await axios.delete(`${API}/admin/categories/${slug}`);
      toast.success('Deleted');
      fetchCategories();
    } catch (err) {
      console.error('Failed to delete category:', err);
      toast.error('Failed');
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>Products</h1>
        <div className="flex items-center gap-3">
          <Button onClick={() => setCatDialogOpen(true)} variant="outline" className="text-sm" data-testid="manage-categories-btn"><Tag size={16} className="mr-2" />Categories</Button>
          <BulkImport onComplete={fetchProducts} />
          <Button onClick={openNew} className="btn-gold text-sm" data-testid="add-product-btn"><Plus size={16} className="mr-2" />Add Product</Button>
        </div>
      </div>
      <div className="bg-white rounded-sm border overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Image</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Mint</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Weight</TableHead>
              <TableHead>Pricing</TableHead>
              <TableHead>Featured</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map(p => (
              <TableRow key={p.id}>
                <TableCell>
                  {p.image_url ? <img src={p.image_url} alt={p.name} className="w-10 h-10 object-cover rounded-sm" /> : <div className="w-10 h-10 bg-gray-100 rounded-sm" />}
                </TableCell>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="text-sm text-gray-500">{p.mint || '-'}</TableCell>
                <TableCell className="capitalize">{p.category}</TableCell>
                <TableCell>{p.weight} {p.weight_unit}</TableCell>
                <TableCell className="text-sm">
                  {p.pricing_mode === 'fixed' && p.fixed_price_nzd ? (
                    <span className="text-green-700 font-medium">NZ${p.fixed_price_nzd.toLocaleString()}</span>
                  ) : (
                    <span className="text-gray-400">Quote</span>
                  )}
                </TableCell>
                <TableCell>{p.featured ? 'Yes' : 'No'}</TableCell>
                <TableCell><span className={`text-xs px-2 py-1 rounded ${p.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{p.status}</span></TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(p)} className="p-1 hover:text-[#D4AF37]" data-testid={`edit-product-${p.id}`}><Pencil size={16} /></button>
                    <button onClick={() => handleDelete(p.id)} className="p-1 hover:text-red-500" data-testid={`delete-product-${p.id}`}><Trash2 size={16} /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Product Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Poppins', sans-serif" }}>{editing ? 'Edit Product' : 'New Product'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="product-name" /></div>
            <div><Label>Description *</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} data-testid="product-description" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={v => {
                  // Copper is measured in AVDP oz per industry standard; auto-select the unit
                  // when switching to copper (unless admin already picked g/kg explicitly).
                  const next = { ...form, category: v };
                  if (v === 'copper' && ['oz', ''].includes(form.weight_unit || 'oz')) {
                    next.weight_unit = 'oz_avdp';
                  }
                  setForm(next);
                }}>
                  <SelectTrigger data-testid="product-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-1"><Label>Weight</Label><Input type="number" step="0.001" value={form.weight} onChange={e => setForm({...form, weight: parseFloat(e.target.value)||0})} data-testid="product-weight" /></div>
                <div className="col-span-1"><Label>Unit</Label>
                  <Select value={form.weight_unit || 'oz'} onValueChange={v => setForm({...form, weight_unit: v})}>
                    <SelectTrigger data-testid="product-weight-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="oz">oz (troy ounce)</SelectItem>
                      <SelectItem value="oz_avdp">oz avdp (avoirdupois — for copper & base metals)</SelectItem>
                      <SelectItem value="g">g (grams)</SelectItem>
                      <SelectItem value="kg">kg (kilograms)</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.category === 'copper' && form.weight_unit !== 'oz_avdp' && (
                    <p className="text-[10px] text-amber-600 mt-1" data-testid="copper-avdp-hint">
                      Copper is typically measured in AVDP oz — consider switching.
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Mint</Label><Input value={form.mint} onChange={e => setForm({...form, mint: e.target.value})} placeholder="e.g. Perth Mint" data-testid="product-mint" /></div>
              <div><Label>Purity</Label><Input value={form.purity} onChange={e => setForm({...form, purity: e.target.value})} placeholder="e.g. .9999" data-testid="product-purity" /></div>
            </div>

            {/* Pricing Section */}
            <div className="border border-gray-200 rounded-sm p-4 space-y-3">
              <Label className="text-sm font-semibold">Pricing Mode</Label>
              <Select value={form.pricing_mode} onValueChange={v => setForm({...form, pricing_mode: v})}>
                <SelectTrigger data-testid="product-pricing-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="quote">Request Quote (no price shown)</SelectItem>
                  <SelectItem value="fixed">Fixed Price (show price + Buy Now)</SelectItem>
                </SelectContent>
              </Select>
              {form.pricing_mode === 'fixed' && (
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Fixed Price (NZD)</Label><Input type="number" step="0.01" value={form.fixed_price_nzd} onChange={e => setForm({...form, fixed_price_nzd: e.target.value})} placeholder="e.g. 8500.00" data-testid="product-fixed-price" /></div>
                  <div><Label>Premium %</Label><Input type="number" step="0.1" value={form.premium_percentage} onChange={e => setForm({...form, premium_percentage: parseFloat(e.target.value)||0})} data-testid="product-premium" /></div>
                </div>
              )}
              {form.pricing_mode === 'fixed' && (
                <p className="text-xs text-gray-400">Fixed prices are locked for 10 minutes per session. After expiry, customers must refresh before checkout.</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div><Label>Sort Order</Label><Input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: parseInt(e.target.value)||0})} data-testid="product-sort" /></div>
              <div className="flex items-center gap-4 pt-6">
                <Switch checked={form.featured} onCheckedChange={v => setForm({...form, featured: v})} data-testid="product-featured" /><Label>Featured</Label>
              </div>
            </div>
            <ImageUpload value={form.image_url} onChange={url => setForm({...form, image_url: url})} label="Product Image" />
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
            <Button onClick={handleSave} className="btn-gold w-full" data-testid="save-product-btn">Save Product</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Categories Management Dialog */}
      <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
        <DialogContent className="max-w-md" data-testid="categories-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Poppins', sans-serif" }}>Manage Categories</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="border rounded-sm divide-y max-h-48 overflow-y-auto">
              {categories.map(c => (
                <div key={c.slug} className="flex items-center justify-between px-3 py-2">
                  <div><span className="font-medium text-sm">{c.label}</span><span className="text-xs text-gray-400 ml-2">({c.slug})</span></div>
                  {!['gold','silver','platinum'].includes(c.slug) && (
                    <button onClick={() => handleDeleteCategory(c.slug)} className="text-gray-300 hover:text-red-500"><Trash2 size={14} /></button>
                  )}
                </div>
              ))}
            </div>
            <div className="border-t pt-4 space-y-2">
              <Label className="text-sm font-semibold">Add New Category</Label>
              <div className="grid grid-cols-3 gap-2">
                <Input value={newCat.label} onChange={e => setNewCat({...newCat, label: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-')})} placeholder="Label" />
                <Input value={newCat.slug} onChange={e => setNewCat({...newCat, slug: e.target.value})} placeholder="slug" className="text-xs" />
                <Button onClick={handleAddCategory} className="btn-gold text-sm" data-testid="add-category-btn">Add</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
